import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { 
  Modal, 
  Table, 
  Button, 
  Form, 
  Input, 
  Select, 
  message, 
  Spin, 
  Alert, 
  Space, 
  Popconfirm, 
  Row, 
  Col, 
  Typography,
  Tooltip,
  AutoComplete
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  TeamOutlined,
  UserAddOutlined,
  SearchOutlined 
} from '@ant-design/icons';

const { Option } = Select;
const { Text } = Typography;

const CareTeamManagementModal = ({
  visible,
  onCancel,
  patientId,
  onCareTeamUpdate
}) => {
  const [careTeamMembers, setCareTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Add/Edit Modal states
  const [isAddEditModalVisible, setIsAddEditModalVisible] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [form] = Form.useForm();
  
  // User search functionality
  const [allSystemUsers, setAllSystemUsers] = useState([]); // Store all users
  const [filteredUsers, setFilteredUsers] = useState([]); // Store filtered results
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [isExternalMember, setIsExternalMember] = useState(false);

  // Predefined roles
  const careTeamRoles = [
    'MD',
    'Nursing',
    'Social Worker',
    'Nutritionist',
    'Physical Therapist',
    'Occupational Therapist',
    'Pharmacist',
    'Case Manager',
    'Mental Health Counselor',
    'Respiratory Therapist',
    'Laboratory',
    'Customer Service',
    'Other'
  ];

  // Fetch care team members
  const fetchCareTeam = useCallback(async () => {
    if (!patientId) return;

    setLoading(true);
    setError(null);

    try {
      // Fetch patient with populated care team
      const response = await axios.get(`/patients/${patientId}?populate=careTeam`);
      const patient = response.data.data.patient;

      console.log('Fetched patient data:', patient);
      console.log('Care team data:', patient.careTeam);

      // Handle both structured and array-based care team
      let members = [];

      if (patient.careTeam && Array.isArray(patient.careTeam)) {
        // New embedded care team structure
        members = patient.careTeam.map((member, index) => {
          console.log('Processing care team member:', member);

          return {
            id: member._id || member.userId || `member_${index}`,
            name: member.name || 'Unknown',
            role: member.role || 'Unknown',
            email: member.email || '',
            phone: member.phone || '',
            department: member.department || '',
            isSystemUser: member.isSystemUser !== false,
            userId: member.userId?._id || member.userId
          };
        });
      }

      console.log('Processed care team members:', members);
      setCareTeamMembers(members);
    } catch (err) {
      console.error('Error fetching care team:', err);
      setError('Failed to load care team members');
      setCareTeamMembers([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  // Load care team when modal opens
  useEffect(() => {
    if (visible && patientId) {
      fetchCareTeam();
    }
  }, [visible, patientId, fetchCareTeam]);

  // Load all users once when modal opens
  const loadAllUsers = useCallback(async () => {
    // Only load if not already loaded
    if (allSystemUsers.length > 0) {
      setUserSearchResults(allSystemUsers);
      return;
    }

    setIsSearchingUsers(true);
    try {
      const response = await axios.get('/users', {
        params: { limit: 1000 } // Get all users
      });
      const users = response.data.data.users || [];
      setAllSystemUsers(users);
      setUserSearchResults(users);
    } catch (err) {
      console.error('Error loading users:', err);
      setAllSystemUsers([]);
      setUserSearchResults([]);
    } finally {
      setIsSearchingUsers(false);
    }
  }, [allSystemUsers]);

  // Local search in loaded users (no API call)
  const searchUsersLocally = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      // Show all users if no search term
      setUserSearchResults(allSystemUsers);
      return;
    }

    // Filter users locally
    const filtered = allSystemUsers.filter(user => {
      const searchLower = searchTerm.toLowerCase();
      return (
        user.name?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower) ||
        user.role?.toLowerCase().includes(searchLower)
      );
    });

    setUserSearchResults(filtered);
  }, [allSystemUsers]);

  // No need for debounce since we're searching locally
  const handleUserSearch = useCallback((value) => {
    searchUsersLocally(value);
  }, [searchUsersLocally]);

  // Show Add/Edit Modal
  const showAddEditModal = async (member = null) => {
    setEditingMember(member);
    setIsExternalMember(false);
    // Load all users when modal opens
    await loadAllUsers();
    
    if (member) {
      form.setFieldsValue({
        name: member.name,
        role: member.role,
        email: member.email,
        phone: member.phone,
        department: member.department,
        userId: member.userId
      });
      setIsExternalMember(!member.isSystemUser);
    } else {
      form.resetFields();
    }
    
    setIsAddEditModalVisible(true);
  };

  // Handle Add/Edit Cancel
  const handleAddEditCancel = () => {
    setIsAddEditModalVisible(false);
    setEditingMember(null);
    form.resetFields();
    setUserSearchResults([]);
  };

  // Handle Add/Edit Submit
  const handleAddEditSubmit = async (values) => {
    setSubmitLoading(true);

    console.log('Form values submitted:', values);
    console.log('Is external member:', isExternalMember);

    try {
      let updatedCareTeam = [...careTeamMembers];

      if (editingMember) {
        // Update existing member
        const index = updatedCareTeam.findIndex(m => m.id === editingMember.id);
        if (index !== -1) {
          updatedCareTeam[index] = {
            ...updatedCareTeam[index],
            ...values,
            isSystemUser: !isExternalMember
          };
        }
      } else {
        // Add new member
        const newMember = {
          id: values.userId || `temp_${Date.now()}`,
          ...values,
          isSystemUser: !isExternalMember,
          userId: values.userId // Ensure userId is properly set
        };
        updatedCareTeam.push(newMember);
      }

      // Update patient's care team with full member objects
      const careTeamData = updatedCareTeam.map(member => ({
        userId: member.isSystemUser ? member.userId : undefined,
        name: member.name,
        role: member.role,
        email: member.email || '',
        phone: member.phone || '',
        department: member.department || '',
        isSystemUser: member.isSystemUser
      }));

      console.log('Updating care team with data:', careTeamData);

      await axios.patch(`/patients/${patientId}`, {
        careTeam: careTeamData
      });

      message.success(editingMember ? 'Care team member updated successfully!' : 'Care team member added successfully!');
      
      handleAddEditCancel();
      fetchCareTeam();
      
      // Notify parent component
      if (onCareTeamUpdate) {
        onCareTeamUpdate();
      }
    } catch (err) {
      console.error('Error saving care team member:', err);
      message.error(err.response?.data?.message || 'Failed to save care team member');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle Remove Member
  const handleRemoveMember = async (memberId) => {
    try {
      const updatedMembers = careTeamMembers.filter(m => m.id !== memberId);

      // Update patient's care team with full member objects
      const careTeamData = updatedMembers.map(member => ({
        userId: member.isSystemUser ? member.userId : undefined,
        name: member.name,
        role: member.role,
        email: member.email || '',
        phone: member.phone || '',
        department: member.department || '',
        isSystemUser: member.isSystemUser
      }));

      await axios.patch(`/patients/${patientId}`, {
        careTeam: careTeamData
      });

      message.success('Care team member removed successfully!');
      fetchCareTeam();
      
      // Notify parent component
      if (onCareTeamUpdate) {
        onCareTeamUpdate();
      }
    } catch (err) {
      console.error('Error removing care team member:', err);
      message.error('Failed to remove care team member');
    }
  };

  // Handle user selection from search
  const handleUserSelect = (value, option) => {
    console.log('User selected:', value, option);
    const selectedUser = userSearchResults.find(user => user._id === option.key);
    console.log('Found selected user:', selectedUser);

    if (selectedUser) {
      const formValues = {
        name: selectedUser.name,
        role: selectedUser.role,
        email: selectedUser.email,
        phone: selectedUser.phone || '',
        department: selectedUser.department || '',
        userId: selectedUser._id
      };
      console.log('Setting form values:', formValues);

      form.setFieldsValue(formValues);
      setIsExternalMember(false);
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <div>
          <Text strong style={{ color: '#1890ff' }}>{text}</Text>
          {!record.isSystemUser && (
            <Text type="secondary" style={{ fontSize: '12px', marginLeft: 8 }}>
              (External)
            </Text>
          )}
        </div>
      )
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role'
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email'
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone'
    },
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => showAddEditModal(record)}
              size="small"
            />
          </Tooltip>
          <Tooltip title="Remove">
            <Popconfirm
              title="Are you sure you want to remove this care team member?"
              onConfirm={() => handleRemoveMember(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      )
    }
  ];

  return (
    <>
      <Modal
        title={
          <Space>
            <TeamOutlined />
            Manage Care Team
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        footer={[
          <Button key="close" onClick={onCancel}>
            Close
          </Button>
        ]}
        width={1000}
        destroyOnClose
      >
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between" align="middle">
            <Col>
              <Text type="secondary">
                Manage care team members for this patient
              </Text>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => showAddEditModal()}
              >
                Add Care Team Member
              </Button>
            </Col>
          </Row>
        </div>

        {error && (
          <Alert 
            message={error} 
            type="error" 
            showIcon 
            style={{ marginBottom: 16 }} 
          />
        )}

        <Table
          columns={columns}
          dataSource={careTeamMembers}
          rowKey="id"
          loading={loading}
          pagination={false}
          size="small"
          scroll={{ y: 400 }}
        />
      </Modal>

      {/* Add/Edit Care Team Member Modal */}
      <Modal
        title={editingMember ? 'Edit Care Team Member' : 'Add Care Team Member'}
        open={isAddEditModalVisible}
        onCancel={handleAddEditCancel}
        footer={null}
        destroyOnClose
        width={600}
      >
        <Spin spinning={submitLoading}>
          <Form form={form} layout="vertical" onFinish={handleAddEditSubmit}>
            <Form.Item
              label={
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>Team Member</span>
                  <Button
                    type="link"
                    size="small"
                    onClick={() => setIsExternalMember(!isExternalMember)}
                    style={{ padding: 0, height: 'auto' }}
                  >
                    {isExternalMember ? 'Switch to System User' : 'Switch to External Member'}
                  </Button>
                </div>
              }
            >
              {isExternalMember ? (
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: 'Name is required' }]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="Enter member name" />
                </Form.Item>
              ) : (
                <Form.Item
                  name="name"
                  rules={[{ required: true, message: 'Please select a system user' }]}
                  style={{ marginBottom: 0 }}
                >
                  <AutoComplete
                    placeholder="Click to see all users or type to search..."
                    onSearch={handleUserSearch}
                    onSelect={handleUserSelect}
                    onFocus={() => setUserSearchResults(allSystemUsers)}
                    loading={isSearchingUsers}
                    options={userSearchResults.map(user => ({
                      key: user._id,
                      value: user.name,
                      label: `${user.name} (${user.role})`
                    }))}
                    filterOption={false} // We handle filtering ourselves
                    dropdownMatchSelectWidth={true}
                    style={{ width: '100%' }}
                    notFoundContent={isSearchingUsers ? 'Loading...' : 'No users found'}
                  />
                </Form.Item>
              )}
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="role"
                  label="Role"
                  rules={[{ required: true, message: 'Role is required' }]}
                >
                  <Select placeholder="Select role">
                    {careTeamRoles.map(role => (
                      <Option key={role} value={role}>{role}</Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="department"
                  label="Department"
                >
                  <Input placeholder="Department/Specialization" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input placeholder="Email address" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="phone"
                  label="Phone"
                >
                  <Input placeholder="Phone number" />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="userId" hidden>
              <Input />
            </Form.Item>

            <Form.Item>
              <Button type="primary" htmlType="submit" loading={submitLoading}>
                {editingMember ? 'Update Member' : 'Add Member'}
              </Button>
              <Button style={{ marginLeft: 8 }} onClick={handleAddEditCancel}>
                Cancel
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>
    </>
  );
};

// Debounce utility function
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default CareTeamManagementModal;
